import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Avatar,
    Stack,
    IconButton,
    Tooltip,
    Switch,
    FormControlLabel
} from '@mui/material';
import { Edit, Delete, Visibility } from '@mui/icons-material';

const BadgeCard = ({
    badge,
    onEdit,
    onDelete,
    onToggleStatus,
    onView
}) => {
    const getRarityColor = (rarity) => {
        const colors = {
            COMMON: { bg: '#4caf50', text: 'white' },
            RARE: { bg: '#2196f3', text: 'white' },
            EPIC: { bg: '#ff9800', text: 'white' },
            LEGENDARY: { bg: '#e91e63', text: 'white' }
        };
        return colors[rarity] || colors.COMMON;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            ACHIEVEMENT: '🏆',
            MILESTONE: '🏁',
            STREAK: '🔥',
            ACCURACY: '🎯',
            SPECIAL: '⭐'
        };
        return icons[category] || '🏆';
    };

    const rarityStyle = getRarityColor(badge.rarity);

    return (
        <Card
            elevation={3}
            sx={{
                height: '100%',
                borderRadius: 3,
                border: badge.isActive ? '2px solid transparent' : '2px solid #e0e0e0',
                opacity: badge.isActive ? 1 : 0.7,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
            }}
        >
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header with Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{
                            background: 'transparent',
                            fontSize: '2rem',
                            width: 50,
                            height: 50
                        }}>
                            {badge.iconUrl || getCategoryIcon(badge.category)}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                {badge.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ID: {badge.id}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => onView(badge)} color="info">
                                <Visibility fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Badge">
                            <IconButton size="small" onClick={() => onEdit(badge)} color="primary">
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Badge">
                            <IconButton size="small" onClick={() => onDelete(badge)} color="error">
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Description */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, flexGrow: 1, lineHeight: 1.4 }}
                >
                    {badge.description}
                </Typography>

                {/* Category and Rarity Chips */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        label={badge.category}
                        size="small"
                        sx={{
                            background: 'rgba(67, 160, 71, 0.1)',
                            color: '#43a047',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                        }}
                    />
                    <Chip
                        label={badge.rarity}
                        size="small"
                        sx={{
                            background: rarityStyle.bg,
                            color: rarityStyle.text,
                            fontWeight: 600,
                            fontSize: '0.75rem'
                        }}
                    />
                </Stack>

                {/* Requirements */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Requirements:
                    </Typography>
                    <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                        {badge.requiredClassifications > 0 && (
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                📊 {badge.requiredClassifications} classifications
                            </Typography>
                        )}
                        {badge.requiredStreakDays > 0 && (
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                🔥 {badge.requiredStreakDays} day streak
                            </Typography>
                        )}
                        {badge.requiredAccuracy > 0 && (
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                🎯 {badge.requiredAccuracy}% accuracy
                            </Typography>
                        )}
                        {badge.requiredFeedbacks > 0 && (
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                💬 {badge.requiredFeedbacks} feedbacks
                            </Typography>
                        )}
                        {badge.pointsRequired > 0 && (
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                ⭐ {badge.pointsRequired} points
                            </Typography>
                        )}
                    </Stack>
                </Box>

                {/* Status Toggle */}
                <Box sx={{ mt: 'auto' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={badge.isActive}
                                onChange={() => onToggleStatus(badge)}
                                color="success"
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="caption" fontWeight={600}>
                                {badge.isActive ? 'Active' : 'Inactive'}
                            </Typography>
                        }
                    />
                </Box>

                {/* Created Date */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Created: {new Date(badge.createdAt).toLocaleDateString()}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default BadgeCard;
